import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { cn } from '../utils/cn'

// Extended product data with full details
const productsData = {
  1: {
    id: 1,
    name: 'Rose Quartz',
    category: 'NAKHOONS',
    price: 49,
    image: '/images/product-1.png',
    description: 'Embrace the gentle energy of rose quartz with these elegantly crafted press-on nails. Each nail features a delicate pink hue with subtle shimmer, finished with our signature NFC chip for seamless digital connectivity.',
    details: [
      'Premium quality press-on nails',
      'Integrated NFC technology',
      'Lasts up to 2 weeks with proper care',
      'Includes 24 nails in various sizes',
      'Comes with adhesive tabs and nail glue',
      'Reusable up to 3 times'
    ],
    colors: ['Rose Pink', 'Blush', 'Champagne'],
    sizes: ['XS', 'S', 'M', 'L'],
  },
  2: {
    id: 2,
    name: 'Midnight Black',
    category: 'NAKHOONS',
    price: 49,
    image: '/images/product-1.png',
    description: 'Make a bold statement with our Midnight Black collection. These sleek, sophisticated nails feature a deep black finish with a glossy topcoat, embedded with NFC technology for the modern minimalist.',
    details: [
      'Premium quality press-on nails',
      'Integrated NFC technology',
      'Lasts up to 2 weeks with proper care',
      'Includes 24 nails in various sizes',
      'Comes with adhesive tabs and nail glue',
      'Reusable up to 3 times'
    ],
    colors: ['Jet Black', 'Onyx', 'Charcoal'],
    sizes: ['XS', 'S', 'M', 'L'],
  },
  3: {
    id: 3,
    name: 'Pearl White',
    category: 'NAKHOONS',
    price: 49,
    image: '/images/product-1.png',
    description: 'Timeless elegance meets modern technology. Our Pearl White nails offer a pristine, luminescent finish that catches light beautifully. Perfect for weddings, galas, or everyday luxury.',
    details: [
      'Premium quality press-on nails',
      'Integrated NFC technology',
      'Lasts up to 2 weeks with proper care',
      'Includes 24 nails in various sizes',
      'Comes with adhesive tabs and nail glue',
      'Reusable up to 3 times'
    ],
    colors: ['Pearl', 'Ivory', 'Cream'],
    sizes: ['XS', 'S', 'M', 'L'],
  },
  4: {
    id: 4,
    name: 'Golden Hour',
    category: 'NAKHOONS',
    price: 59,
    image: '/images/product-1.png',
    description: 'Capture the warmth of sunset with our Golden Hour collection. These nails feature a stunning gradient from soft gold to rich amber, with subtle metallic accents that glow in any light.',
    details: [
      'Premium quality press-on nails',
      'Integrated NFC technology',
      'Lasts up to 2 weeks with proper care',
      'Includes 24 nails in various sizes',
      'Comes with adhesive tabs and nail glue',
      'Reusable up to 3 times'
    ],
    colors: ['Gold', 'Amber', 'Bronze'],
    sizes: ['XS', 'S', 'M', 'L'],
  },
}

function ProductImage({ image, name }) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  return (
    <div className="relative aspect-square bg-bg-tertiary overflow-hidden">
      {imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary">
          <span className="text-text-muted text-sm">Product Image</span>
        </div>
      )}

      {!imageError && (
        <img
          src={image}
          alt={name}
          className={cn(
            "absolute inset-0 w-full h-full object-cover object-center",
            imageLoaded ? "opacity-100" : "opacity-0",
            "transition-opacity duration-500"
          )}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}

      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary">
          <div className="w-8 h-8 border-2 border-text-muted/30 border-t-text-muted rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}

export default function ProductPage() {
  const { id } = useParams()
  const product = productsData[id] || productsData[1]
  const [selectedSize, setSelectedSize] = useState('M')
  const [selectedColor, setSelectedColor] = useState(product.colors[0])
  const [quantity, setQuantity] = useState(1)

  return (
    <div className="min-h-screen bg-bg-primary pt-20 lg:pt-24">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <motion.nav
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-2 text-xs uppercase tracking-wider"
        >
          <Link to="/" className="text-text-muted hover:text-text-primary transition-colors">
            Home
          </Link>
          <span className="text-text-muted">/</span>
          <Link to="/" className="text-text-muted hover:text-text-primary transition-colors">
            Shop
          </Link>
          <span className="text-text-muted">/</span>
          <span className="text-text-primary">{product.name}</span>
        </motion.nav>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ProductImage image={product.image} name={product.name} />

            {/* Thumbnail strip - for future use */}
            <div className="flex gap-3 mt-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={cn(
                    "w-20 h-20 bg-bg-tertiary border cursor-pointer transition-all duration-200",
                    i === 1 ? "border-text-primary" : "border-border hover:border-text-muted"
                  )}
                >
                  <img
                    src={product.image}
                    alt={`${product.name} view ${i}`}
                    className="w-full h-full object-cover opacity-80"
                  />
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col"
          >
            {/* Category & Name */}
            <p className="text-xs uppercase tracking-[0.2em] text-text-muted mb-2">
              {product.category}
            </p>
            <h1 className="text-3xl lg:text-4xl font-serif text-text-primary mb-4">
              {product.name}
            </h1>

            {/* Price */}
            <p className="text-2xl font-display text-text-primary mb-6">
              ${product.price}
            </p>

            {/* Description */}
            <p className="text-text-secondary leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Divider */}
            <div className="w-full h-px bg-border mb-8" />

            {/* Color Selection */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.15em] text-text-primary mb-3">
                Color: <span className="text-text-secondary">{selectedColor}</span>
              </p>
              <div className="flex gap-3">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={cn(
                      "px-4 py-2 text-xs uppercase tracking-wider border transition-all duration-200",
                      selectedColor === color
                        ? "bg-text-primary text-white border-text-primary"
                        : "bg-transparent text-text-secondary border-border hover:border-text-primary"
                    )}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Selection */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-[0.15em] text-text-primary">
                  Size: <span className="text-text-secondary">{selectedSize}</span>
                </p>
                <button className="text-xs uppercase tracking-wider text-text-muted underline hover:text-text-primary transition-colors">
                  Size Guide
                </button>
              </div>
              <div className="flex gap-3">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={cn(
                      "w-12 h-12 text-xs uppercase tracking-wider border transition-all duration-200",
                      selectedSize === size
                        ? "bg-text-primary text-white border-text-primary"
                        : "bg-transparent text-text-secondary border-border hover:border-text-primary"
                    )}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex gap-4 mb-8">
              {/* Quantity Selector */}
              <div className="flex items-center border border-border">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 12H4" />
                  </svg>
                </button>
                <span className="w-12 h-12 flex items-center justify-center text-text-primary font-medium">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Add to Cart Button */}
              <Link
                to="/cart"
                className={cn(
                  "flex-1 flex items-center justify-center gap-2",
                  "h-12 px-8",
                  "bg-text-primary text-white",
                  "text-sm font-medium uppercase tracking-wider",
                  "hover:bg-primary-light transition-colors duration-300"
                )}
              >
                Add to Cart
              </Link>
            </div>

            {/* Buy Now */}
            <Link
              to="/checkout"
              className={cn(
                "w-full flex items-center justify-center gap-2",
                "h-12 px-8",
                "bg-transparent text-text-primary border border-text-primary",
                "text-sm font-medium uppercase tracking-wider",
                "hover:bg-text-primary hover:text-white transition-all duration-300",
                "mb-8"
              )}
            >
              Buy Now
            </Link>

            {/* Product Details Accordion */}
            <div className="border-t border-border pt-6">
              <h3 className="text-xs uppercase tracking-[0.15em] text-text-primary mb-4">
                Product Details
              </h3>
              <ul className="space-y-2">
                {product.details.map((detail, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-text-secondary">
                    <span className="w-1 h-1 mt-2 bg-text-muted rounded-full flex-shrink-0" />
                    {detail}
                  </li>
                ))}
              </ul>
            </div>

            {/* NFC Info */}
            <div className="border-t border-border mt-6 pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-bg-tertiary flex items-center justify-center">
                  <svg className="w-6 h-6 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">NFC Enabled</p>
                  <p className="text-xs text-text-muted">Tap to pay, share contacts & more</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Related Products Section */}
      <section className="bg-bg-secondary py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-xs uppercase tracking-[0.3em] text-text-muted mb-4"
          >
            You May Also Like
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-center text-2xl font-serif text-text-primary mb-12"
          >
            Complete Your Collection
          </motion.p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.values(productsData)
              .filter((p) => p.id !== parseInt(id))
              .slice(0, 4)
              .map((p, index) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/product/${p.id}`} className="group block">
                    <div className="relative aspect-square bg-bg-tertiary mb-4 overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <p className="text-xs uppercase tracking-[0.15em] text-text-muted mb-1">
                      {p.category}
                    </p>
                    <h3 className="text-sm font-medium text-text-primary uppercase tracking-wider">
                      {p.name}
                    </h3>
                    <p className="text-sm text-text-secondary mt-1">${p.price}</p>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  )
}
